import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchFlightStatusApi } from '@/api/flightStatus'
import {
    FlightStatusState,
    FlightStatusQueryParams,
    FlightStatusCardData,
    SearchIntent,
    FlightStatusFilterData,
    SelectedFlightStatusFilters,
    ActiveTab,
    SearchType,
    INITIAL_FILTERS,
} from '@/containers/FlightStatusSrp/types'
import { FLIGHT_STATUS_CONSTANTS, TIME_BUCKET_RANGES } from '@/containers/FlightStatusSrp/constants'
import { RootState } from '@/redux/store/configureStore'

function toHttpStatus(value: unknown): number {
    const n = typeof value === 'number' ? value : Number(value)
    return Number.isFinite(n) && n >= 100 && n <= 599 ? n : 500
}

const initialState: FlightStatusState = {
    searchIntent: null,
    cards: [],
    filterData: null,
    selectedFilters: { ...INITIAL_FILTERS },
    isLoading: false,
    error: '',
    activeTab: 'departures',
    searchType: null,
}

export const fetchFlightStatusData = createAsyncThunk(
    'flightStatus/fetch',
    async (params: FlightStatusQueryParams) => {
        try {
            const response = await fetchFlightStatusApi(params)
            const httpStatus = toHttpStatus(response?.status)
            if (httpStatus !== 200) {
                return { status: httpStatus, data: null as null }
            }
            const data = response.data
            if (!data) {
                return { status: 500, data: null as null }
            }
            return { status: 200, data }
        } catch {
            return { status: 500, data: null as null }
        }
    }
)

export const flightStatusSlice = createSlice({
    name: 'flightStatus',
    initialState,
    reducers: {
        setFlightStatusLoading(state, action: PayloadAction<boolean>) {
            state.isLoading = action.payload
        },
        setFlightStatusError(state, action: PayloadAction<string>) {
            state.isLoading = false
            state.error = action.payload
        },
        setSearchIntent(state, action: PayloadAction<SearchIntent>) {
            state.searchIntent = action.payload
        },
        setFlightStatusCards(state, action: PayloadAction<FlightStatusCardData[]>) {
            state.cards = action.payload
        },
        setFilterData(state, action: PayloadAction<FlightStatusFilterData>) {
            state.filterData = action.payload
        },
        setSelectedFilters(state, action: PayloadAction<SelectedFlightStatusFilters>) {
            state.selectedFilters = action.payload
        },
        setActiveTab(state, action: PayloadAction<ActiveTab>) {
            state.activeTab = action.payload
        },
        setSearchType(state, action: PayloadAction<SearchType>) {
            state.searchType = action.payload
        },
        resetFilters(state) {
            state.selectedFilters = { ...INITIAL_FILTERS }
        },
        flushFlightStatusData() {
            return { ...initialState }
        },
    },
    extraReducers(builder) {
        builder.addCase(fetchFlightStatusData.pending, state => {
            state.isLoading = true
            state.error = ''
        })
        builder.addCase(fetchFlightStatusData.fulfilled, (state, action) => {
            const { status, data } = action.payload || {}
            state.isLoading = false

            if (status === 200 && data) {
                state.searchIntent = data.searchIntent || null
                const rawCards = data.cards || []
                const seen = new Set<string>()
                state.cards = rawCards.filter((card: FlightStatusCardData) => {
                    if (seen.has(card.flightTrackerId)) return false
                    seen.add(card.flightTrackerId)
                    return true
                })
                state.filterData = data.filterData || null
                state.selectedFilters = { ...INITIAL_FILTERS }
                state.error =
                    state.cards.length === 0 ? FLIGHT_STATUS_CONSTANTS.ERROR.NO_FLIGHTS : ''
            } else {
                const code = toHttpStatus(status)
                state.searchIntent = null
                state.cards = []
                state.filterData = null
                state.selectedFilters = { ...INITIAL_FILTERS }
                state.error =
                    code >= 500
                        ? FLIGHT_STATUS_CONSTANTS.ERROR.SERVER_ERROR
                        : FLIGHT_STATUS_CONSTANTS.ERROR.FETCH_FAILED
            }
        })
        builder.addCase(fetchFlightStatusData.rejected, state => {
            state.isLoading = false
            state.cards = []
            state.error = FLIGHT_STATUS_CONSTANTS.ERROR.SERVER_ERROR
        })
    },
})

const { reducer, actions } = flightStatusSlice

export const {
    setFlightStatusLoading,
    setFlightStatusError,
    setSearchIntent,
    setFlightStatusCards,
    setFilterData,
    setSelectedFilters,
    setActiveTab,
    setSearchType,
    resetFilters,
    flushFlightStatusData,
} = actions

export const getFlightStatusState = (state: RootState) => state.flightStatus

const matchesTimeBucket = (
    scheduledTime: string,
    updatedTime: string | null | undefined,
    selectedBuckets: string[]
): boolean => {
    if (selectedBuckets.length === 0) return true
    try {
        const date = new Date(updatedTime || scheduledTime)
        const hhmm = date.getHours() * 100 + date.getMinutes()
        return selectedBuckets.some(bucket => {
            const range = TIME_BUCKET_RANGES[bucket]
            return range && hhmm >= range.start && hhmm < range.end
        })
    } catch {
        return true
    }
}

export const getFilteredCards = createSelector(
    [getFlightStatusState],
    ({ cards, selectedFilters }): FlightStatusCardData[] => {
        let filtered: FlightStatusCardData[] = cards

        if (selectedFilters.airlines.length > 0) {
            filtered = filtered.filter((c: FlightStatusCardData) =>
                selectedFilters.airlines.includes(c.airline.airlineCode)
            )
        }
        if (selectedFilters.departureTime.length > 0) {
            filtered = filtered.filter((c: FlightStatusCardData) =>
                matchesTimeBucket(c.departure.scheduledTime, c.departure.updatedTime, selectedFilters.departureTime)
            )
        }
        if (selectedFilters.arrivalTime.length > 0) {
            filtered = filtered.filter((c: FlightStatusCardData) =>
                matchesTimeBucket(c.arrival.scheduledTime, c.arrival.updatedTime, selectedFilters.arrivalTime)
            )
        }

        return filtered
    }
)

export default reducer
